// The stereonet as a standalone SVG (bands as an embedded image, everything else vector),
// as DipField 1.0.0 exports it; the contour layer is derived.ts contourPath(), so the
// parity test checks the exported lines. GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import { t } from "../../i18n/index.js";
import { esc } from "../../lib/dom.js";
import { fmt } from "../../lib/format.js";
import { css, setColor } from "../../lib/theme.js";
import { contourPath } from "../../state/derived.js";
import { APP } from "../../state/engines.js";
import { X, P } from "./ctx.js";
import { densityImage } from "./net.js";
import { rosePetalsNet, roseSVG } from "./rose.js";
import { methodName } from "./panels.js";

/** The stereonet as SVG; `footer` adds the project and app name under it (the export does,
 *  the report does not: it names the software on its cover only). */
export function stereonetSVG(footer = true): string {
  const p = P(), st = p.stereo, disp = st.display, proj = st.projection;
  const W = 720, c0 = W / 2, Rr = 300, T = (q: S.XY) => `${(c0 + q.x * Rr).toFixed(2)},${(c0 - q.y * Rr).toFixed(2)}`;
  const ink = css("--ink");
  // same first-free-slot placement as the screen, with an estimated glyph width
  const boxes: { x0: number; y0: number; x1: number; y1: number }[] = [];
  const svgLabel = (x: number, y: number, text: string, col: string, size = 13) => {
    const w = text.length * size * 0.62, h = size * 1.05, d = size * 0.55;
    const slots: [number, number, string][] = [[x + d, y - d, "start"], [x + d, y + d + h, "start"], [x - d, y - d, "end"], [x - d, y + d + h, "end"], [x, y - d - 4, "middle"], [x, y + d + 4 + h, "middle"]];
    boxes.push({ x0: x - 6, y0: y - 6, x1: x + 6, y1: y + 6 });
    for (const [lx, ly, an] of slots) {
      const x0 = an === "start" ? lx : an === "end" ? lx - w : lx - w / 2, y0 = ly - h;
      const box = { x0: x0 - 2, y0: y0 - 1, x1: x0 + w + 2, y1: ly + 1 };
      if (boxes.some((b) => box.x0 < b.x1 && box.x1 > b.x0 && box.y0 < b.y1 && box.y1 > b.y0)) continue;
      boxes.push(box);
      return `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="${size}" font-weight="600" text-anchor="${an}" fill="${col}" paint-order="stroke" stroke="#fff" stroke-width="3" stroke-linejoin="round">${esc(text)}</text>`;
    }
    return "";
  };
  let s = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${W + 90}" viewBox="0 0 ${W} ${W + 90}" font-family="Barlow, Anuphan, Arial, sans-serif">`;
  s += `<defs><clipPath id="dfnetclip"><circle cx="${c0}" cy="${c0}" r="${Rr}"/></clipPath></defs><rect width="100%" height="100%" fill="#fff"/>`;
  s += `<g clip-path="url(#dfnetclip)">`;
  if (disp.contours && X.grid) {
    const img = densityImage(2 * Rr * 2);
    if (img) s += `<image href="${img.toDataURL("image/png")}" x="${c0 - Rr}" y="${c0 - Rr}" width="${2 * Rr}" height="${2 * Rr}"/>`;
    s += `<path fill="none" stroke="${css("--contour-line")}" stroke-width="0.6" d="${contourPath(X.grid, X.levels, c0, Rr)}"/>`;
  }
  const rp = rosePetalsNet();
  if (rp) {
    const [m0, m1] = rp.mean ? [T(rp.mean[0]).split(","), T(rp.mean[1]).split(",")] : [[], []];
    s += `<g fill="${rp.color}" fill-opacity="0.25" stroke="${rp.color}" stroke-opacity="0.8">` + rp.pts.map((q) => `<polygon points="${q.map(T).join(" ")}"/>`).join("") + `</g>` +
      (rp.mean ? `<line x1="${m0[0]}" y1="${m0[1]}" x2="${m1[0]}" y2="${m1[1]}" stroke="${ink}" stroke-width="1.4" stroke-dasharray="6 4"/>` : "");
  }
  if (disp.overlay !== "none") s += `<path fill="none" stroke="${css("--grid")}" stroke-width="0.6" d="` +
    S.overlayGrid(disp.overlay, proj).map((l) => l.map((q, i) => (i ? "L" : "M") + T(q)).join("")).join("") + `"/>`;
  if (disp.poles) s += X.polePts.map((q, i) => { const a = X.d.assignment[i];
    const col = !p.sets.length ? ink : a ? setColor(a - 1) : css("--ink-2");
    return `<circle cx="${(c0 + q.x * Rr).toFixed(1)}" cy="${(c0 - q.y * Rr).toFixed(1)}" r="${disp.poleSize}" fill="${col}"/>`; }).join("");
  const svgPlane = (v: S.Vec3, col: string, label: string) => {
    const pl = S.poleToPlane(v), m = S.project(v, proj), mx = c0 + m.x * Rr, my = c0 - m.y * Rr;
    s += `<polyline fill="none" stroke="${col}" stroke-width="1.6" points="${S.greatCircle(pl.dip, pl.dipDir, proj).map(T).join(" ")}"/>` +
      `<path stroke="${col}" stroke-width="1.6" d="M${mx - 6},${my}H${mx + 6}M${mx},${my - 6}V${my + 6}"/>` + svgLabel(mx, my, label, col);
  };
  p.sets.forEach((set, k) => {
    const col = setColor(k), sm = X.d.summaries[k];
    s += `<g clip-path="url(#dfnetclip)"><path fill="none" stroke="${col}" stroke-width="1.2" stroke-dasharray="5 4" d="` +
      S.windowOutline(set.window, proj).map((l) => l.map((q, i) => (i ? "L" : "M") + T(q)).join("")).join("") + `"/></g>`;
    if (sm?.stats && sm.meanPlane) {
      const m = S.project(sm.stats.mean, proj), mx = c0 + m.x * Rr, my = c0 - m.y * Rr;
      if (disp.setPlanes) s += `<polyline fill="none" stroke="${col}" stroke-width="1.8" points="${S.greatCircle(sm.meanPlane.dip, sm.meanPlane.dipDir, proj).map(T).join(" ")}"/>`;
      s += `<rect x="${mx - 4}" y="${my - 4}" width="8" height="8" fill="${col}" stroke="#fff" stroke-width="1.5"/>` + svgLabel(mx, my, set.name, col);
    }
  });
  if (disp.globalMean && X.d.gm) svgPlane(X.d.gm, css("--gm"), "gm");
  if (disp.bestFit && X.d.bm) svgPlane(X.d.bm, css("--bm"), "bm");
  s += `</g><circle cx="${c0}" cy="${c0}" r="${Rr}" fill="none" stroke="${ink}" stroke-width="1.4"/>`;
  for (let a = 0; a < 360; a += 10) {
    const tt = a * S.DEG, len = a % 90 === 0 ? 10 : a % 30 === 0 ? 6 : 3.5;
    s += `<line x1="${c0 + Math.sin(tt) * Rr}" y1="${c0 - Math.cos(tt) * Rr}" x2="${c0 + Math.sin(tt) * (Rr + len)}" y2="${c0 - Math.cos(tt) * (Rr + len)}" stroke="${ink}"/>`;
  }
  s += `<g font-size="16" font-weight="600" text-anchor="middle" fill="${ink}"><text x="${c0}" y="${c0 - Rr - 22}">N</text><text x="${c0}" y="${c0 + Rr + 32}">S</text><text x="${c0 + Rr + 24}" y="${c0 + 6}">E</text><text x="${c0 - Rr - 24}" y="${c0 + 6}">W</text></g>`;
  const info = t("{n} poles · {proj}", { n: p.data.planes.length, proj: `${proj.method === "equal-area" ? t("Equal area") : t("Equal angle")}, ${proj.hemisphere === "lower" ? t("lower hemisphere") : t("upper hemisphere")}` }) +
    (X.grid && disp.contours ? " · " + t("{method}, counting circle {c}%, max {m}%", { method: methodName(), c: fmt(st.density.countCircle * 100, 1), m: fmt(X.grid.max, 2) }) : "");
  s += `<text x="${c0}" y="${W + 50}" font-size="14" text-anchor="middle" fill="${ink}">${esc(info)}</text>`;
  if (footer) s += `<text x="${c0}" y="${W + 72}" font-size="11" text-anchor="middle" fill="${css("--ink-2")}">${esc(p.meta.name)} · ${APP.app} ${APP.version}</text>`;
  s += `</svg>`;
  return s;
}

export function currentSVG(): { name: string; svg: string } | null {
  const p = P(); if (!p.data.planes.length) return null;
  return p.stereo.display.view === "rose" ? { name: "dipfield-rosette", svg: roseSVG(720, true) } : { name: "dipfield-stereonet", svg: stereonetSVG() };
}
